import { ref, readonly } from "vue";
import { CcPlatformSdk, MemoryTokenProvider } from "@cc-consulting-nv/ccsdk";
import type { AuthTokens, SessionStore } from "@cc-consulting-nv/ccsdk";
import { resolveApiBaseUrl } from "@/utils/env";
import { useAuthStorage } from "@/composables/useAuthStorage";
import {
  AUTH_USER_REFRESHED_EVENT,
  mapCurrentUserToAuthUser,
} from "@/services/authUserProfile";
import type { AuthUser } from "@/types";

// SDK singleton instance
let sdkInstance: CcPlatformSdk | null = null;

// Reactive state for SDK initialization
const isInitialized = ref(false);
const initError = ref<string | null>(null);

// Storage keys (matching authService to avoid circular dependency)
const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "auth_user";
const authStorage = useAuthStorage();
let pendingAuthUserRefresh: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Cross-tab token synchronisation via BroadcastChannel
// ---------------------------------------------------------------------------
// When one tab refreshes the access token, it broadcasts to all other tabs so
// they can skip a redundant /auth/refresh call (which would fail if the server
// rotated the refresh cookie). Logout is also broadcast so all tabs redirect.
// ---------------------------------------------------------------------------
const AUTH_CHANNEL_NAME = "cc-auth-sync";

type AuthChannelMessage =
  | { type: "token-refreshed"; accessToken: string }
  | { type: "logout" };

let authChannel: BroadcastChannel | null = null;

// ---------------------------------------------------------------------------
// iOS Safari / WKWebView IndexedDB resume guard
// ---------------------------------------------------------------------------
// iOS silently drops the IndexedDB connection when the app is backgrounded
// under memory pressure. Subsequent reads throw:
//   UnknownError: Connection to Indexed Database server lost.
// Force the SDK to reopen its Dexie cache when the page becomes visible (and
// on focus/online for desktop coverage) so the next read succeeds.
// ---------------------------------------------------------------------------
let dexieResumeGuardInstalled = false;

function installDexieResumeGuard() {
  if (dexieResumeGuardInstalled) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  dexieResumeGuardInstalled = true;

  const tryReopen = () => {
    if (document.visibilityState !== "visible") return;
    if (!sdkInstance) return;
    sdkInstance.reopenCache().catch((err) => {
      console.warn("[useSdk] reopenCache failed:", err);
    });
  };

  document.addEventListener("visibilitychange", tryReopen);
  window.addEventListener("focus", tryReopen);
  window.addEventListener("online", tryReopen);
}

function getAuthChannel(): BroadcastChannel | null {
  if (authChannel) return authChannel;
  if (typeof BroadcastChannel === "undefined") return null;

  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);

    authChannel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      const msg = event.data;
      if (!msg?.type) return;

      if (msg.type === "token-refreshed" && msg.accessToken) {
        // Another tab refreshed — inject the token so we don't re-refresh
        sdkInstance?.setTokens({ accessToken: msg.accessToken });
      } else if (msg.type === "logout") {
        // Another tab logged out — clear local state and redirect
        sdkInstance?.setTokens(null);
        localStorage.removeItem(USER_KEY);
        window.location.href = "/login";
      }
    };

    return authChannel;
  } catch {
    // BroadcastChannel not supported or blocked
    return null;
  }
}

function broadcastTokenRefresh(accessToken: string): void {
  getAuthChannel()?.postMessage({
    type: "token-refreshed",
    accessToken,
  } satisfies AuthChannelMessage);
}

function broadcastLogout(): void {
  getAuthChannel()?.postMessage({
    type: "logout",
  } satisfies AuthChannelMessage);
}

/**
 * Get the base URL from environment
 * In production, uses window.location.origin so a single build works on any domain
 */
function getBaseUrl(): string {
  return resolveApiBaseUrl();
}

function isIpLikeHostname(hostname: string): boolean {
  return (
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) ||
    hostname.includes(":")
  );
}

function getSiteKey(hostname: string): string {
  const normalized = hostname.toLowerCase();
  if (!normalized || normalized === "localhost" || isIpLikeHostname(normalized)) {
    return normalized;
  }

  const labels = normalized.split(".").filter(Boolean);
  if (labels.length < 2) {
    return normalized;
  }

  return labels.slice(-2).join(".");
}

function shouldUseRefreshCookieOnWeb(baseUrl: string): boolean {
  if (authStorage.isNative || typeof window === "undefined") {
    return false;
  }

  try {
    const appUrl = new URL(window.location.origin);
    const apiUrl = new URL(baseUrl);

    if (appUrl.origin === apiUrl.origin) {
      return true;
    }

    return getSiteKey(appUrl.hostname) === getSiteKey(apiUrl.hostname);
  } catch {
    return true;
  }
}

/**
 * Get initial tokens from the environment.
 */
function getEnvironmentTokens(): AuthTokens | undefined {
  const envToken = import.meta.env.VITE_API_TOKEN;
  if (envToken) {
    return {
      accessToken: envToken,
    };
  }
  return undefined;
}

function getLegacyWebTokens(): AuthTokens | null {
  if (authStorage.isNative || typeof localStorage === "undefined") {
    return null;
  }

  const accessToken = localStorage.getItem(TOKEN_KEY) || undefined;
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || undefined;

  if (!accessToken && !refreshToken) {
    return null;
  }

  return { accessToken, refreshToken };
}

function clearLegacyWebTokens(): void {
  if (typeof localStorage === "undefined") {
    return;
  }

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function createSessionStore(): SessionStore {
  const baseUrl = getBaseUrl();
  const useRefreshCookie = shouldUseRefreshCookieOnWeb(baseUrl);
  const persistWebTokens = !authStorage.isNative && !useRefreshCookie;

  return {
    async loadTokens() {
      if (authStorage.isNative) {
        const [storedAccessToken, storedRefreshToken] = await Promise.all([
          authStorage.getAuthToken(),
          authStorage.getRefreshToken(),
        ]);

        if (!storedAccessToken && !storedRefreshToken) {
          return null;
        }

        return {
          accessToken: storedAccessToken || undefined,
          refreshToken: storedRefreshToken || undefined,
        };
      }

      if (persistWebTokens) {
        return getLegacyWebTokens();
      }

      // Cookie-mode web: the access token is in-memory only, but we now
      // persist the refresh token so a page reload can rebuild the session
      // by posting it to /auth/refresh. Customer-owned multi-tenant API
      // hosts no longer accept the cookie-only refresh path (cc-social
      // #833), so the body carrier is required.
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || undefined;
      // One-time migration: if a stale access token was persisted under the
      // old non-cookie mode, hydrate it once then drop it from storage.
      const legacyAccessToken = localStorage.getItem(TOKEN_KEY) || undefined;
      if (legacyAccessToken) {
        localStorage.removeItem(TOKEN_KEY);
      }

      if (!refreshToken && !legacyAccessToken) {
        return null;
      }

      return {
        accessToken: legacyAccessToken,
        refreshToken,
      };
    },
    async saveTokens(tokens) {
      if (persistWebTokens) {
        if (tokens?.accessToken) {
          localStorage.setItem(TOKEN_KEY, tokens.accessToken);
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }

        if (tokens?.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        } else {
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }

        return;
      }

      if (!authStorage.isNative) {
        // Cookie-mode web: keep the access token in memory only, but persist
        // the refresh token so reload can re-authenticate via the body of
        // /auth/refresh. The httpOnly cc_refresh_token cookie still rides
        // along, but multi-tenant API hosts won't honor it without the body.
        localStorage.removeItem(TOKEN_KEY);

        if (tokens?.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        } else {
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
        return;
      }

      if (tokens.accessToken) {
        await authStorage.setAuthToken(tokens.accessToken);
      } else {
        await authStorage.removeAuthToken();
      }

      if (tokens.refreshToken) {
        await authStorage.setRefreshToken(tokens.refreshToken);
      } else {
        await authStorage.removeRefreshToken();
      }
    },
    async clearTokens() {
      if (authStorage.isNative) {
        await authStorage.removeAuthTokens();
        return;
      }

      clearLegacyWebTokens();
    },
  };
}

/**
 * Clear all auth data and redirect to login
 */
async function handleTokenExpiration(): Promise<void> {
  console.log(
    "🔒 Token expired or invalid - clearing all data and redirecting to login",
  );
  console.log("🔒 Stack trace:", new Error().stack);

  broadcastLogout();

  try {
    await sdkInstance?.clearSession();
  } catch (error) {
    console.error("Failed to clear session after unauthorized response:", error);
  }

  localStorage.removeItem(USER_KEY);

  // Clear SDK cache
  try {
    await sdkInstance?.clearCache();
    console.log("✅ Cleared SDK cache on token expiration");
  } catch (error) {
    console.error("Failed to clear SDK cache:", error);
  }

  // Redirect to login
  window.location.href = "/login";
}

function publishRefreshedAuthUser(user: AuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthUser>(AUTH_USER_REFRESHED_EVENT, {
      detail: user,
    }),
  );
}

async function refreshCurrentUserSnapshot(): Promise<void> {
  if (!sdkInstance?.getTokens()?.accessToken) {
    return;
  }

  if (pendingAuthUserRefresh) {
    return pendingAuthUserRefresh;
  }

  pendingAuthUserRefresh = (async () => {
    try {
      const profile = await sdkInstance?.getCurrentUser();
      if (!profile) {
        return;
      }

      publishRefreshedAuthUser(mapCurrentUserToAuthUser(profile));
    } catch (error) {
      console.warn("Failed to refresh current user after token refresh", error);
    } finally {
      pendingAuthUserRefresh = null;
    }
  })();

  return pendingAuthUserRefresh;
}

/**
 * Initialize the SDK instance
 */
function initializeSdk(): CcPlatformSdk {
  if (sdkInstance) {
    return sdkInstance;
  }

  const baseUrl = getBaseUrl();
  const tokens = getEnvironmentTokens();
  const useRefreshCookie = shouldUseRefreshCookieOnWeb(baseUrl);

  // Check environment variable for SDK logging (VITE_SDK_ENABLE_LOGGING)
  // Explicitly check for "true" or "1", default to false
  // This explicitly disables logging unless the env var is set to "true" or "1"
  const envLogging = import.meta.env.VITE_SDK_ENABLE_LOGGING;
  const enableLogging = Boolean(
    envLogging === "true" || 
    envLogging === "1" || 
    envLogging === true
  );

  // Check environment variable for MessagePack (VITE_SDK_USE_MSGPACK)
  const envMsgpack = import.meta.env.VITE_SDK_USE_MSGPACK;
  const useMsgpack = Boolean(
    envMsgpack === "true" ||
    envMsgpack === "1" ||
    envMsgpack === true
  );

  sdkInstance = new CcPlatformSdk({
    baseUrl,
    tokens,
    tokenProvider: new MemoryTokenProvider(tokens ?? null),
    sessionStore: createSessionStore(),
    useRefreshCookie,
    enableLogging, // Explicitly pass false if not enabled (overrides any localStorage/window settings)
    useMsgpack, // Enable MessagePack binary format for smaller/faster API responses
    onRefreshTokens: async () => {
      // Check if tokens were already refreshed (e.g., by auth bootstrap) before
      // making another refresh call. This prevents a race where a 401 from a
      // request that fired before bootstrap completes triggers a second refresh
      // after the cookie was already rotated, causing a 400/401 and logout.
      const currentTokens = sdkInstance?.getTokens();
      if (currentTokens?.accessToken) {
        return currentTokens;
      }

      const refreshedTokens = await sdkInstance?.refreshSession();
      if (!refreshedTokens?.accessToken) {
        throw new Error("Token refresh failed");
      }
      broadcastTokenRefresh(refreshedTokens.accessToken);
      queueMicrotask(() => {
        void refreshCurrentUserSnapshot();
      });
      console.log("✅ Token refreshed successfully");
      return refreshedTokens;
    },
    onUnauthorized: async () => {
      // Handle 401 errors after refresh failed - clear all data and redirect to login
      console.warn("Unauthorized after refresh attempt - redirecting to login");
      await handleTokenExpiration();
    },
  });

  isInitialized.value = true;

  installDexieResumeGuard();

  if (!authStorage.isNative && import.meta.env.DEV && !useRefreshCookie) {
    console.warn(
      "[Auth] Cross-site local dev origin detected; using token-based web session storage because refresh cookies are unreliable on this origin.",
      {
        appOrigin: typeof window !== "undefined" ? window.location.origin : "unknown",
        apiBase: baseUrl,
      },
    );
  }

  // Start listening for cross-tab auth events (token refresh, logout)
  getAuthChannel();

  return sdkInstance;
}

/**
 * Composable to access the CC Platform SDK
 */
export function useSdk() {
  // Initialize SDK on first access
  if (!sdkInstance) {
    try {
      initializeSdk();
    } catch (error) {
      initError.value =
        error instanceof Error ? error.message : "Failed to initialize SDK";
      console.error("SDK initialization error:", error);
    }
  }

  /**
   * Set in-memory authentication tokens only.
   * Prefer setSession() when the session should survive reloads.
   */
  function setTokens(tokens: AuthTokens | null) {
    if (sdkInstance) {
      sdkInstance.setTokens(tokens);
    }
  }

  async function setSession(tokens: AuthTokens | null) {
    if (sdkInstance) {
      await sdkInstance.setSession(tokens);
    }
  }

  async function restoreSession(): Promise<AuthTokens | null> {
    if (!sdkInstance) {
      return null;
    }
    return sdkInstance.restoreSession();
  }

  async function refreshSession(): Promise<AuthTokens | null> {
    if (!sdkInstance) {
      return null;
    }
    const refreshedTokens = await sdkInstance.refreshSession();
    if (refreshedTokens?.accessToken) {
      broadcastTokenRefresh(refreshedTokens.accessToken);
      queueMicrotask(() => {
        void refreshCurrentUserSnapshot();
      });
    }
    return refreshedTokens;
  }

  /**
   * Get current authentication tokens
   */
  function getTokens(): AuthTokens | null {
    return sdkInstance?.getTokens() ?? null;
  }

  /**
   * Check if user is authenticated (has tokens)
   */
  function hasTokens(): boolean {
    const tokens = getTokens();
    return Boolean(tokens?.accessToken);
  }

  /**
   * Clear all cached data
   */
  async function clearCache() {
    if (sdkInstance) {
      await sdkInstance.clearCache();
    }
  }

  return {
    // SDK instance (may be null if initialization failed)
    sdk: sdkInstance!,

    // State
    isInitialized: readonly(isInitialized),
    initError: readonly(initError),

    // Methods
    setTokens,
    setSession,
    restoreSession,
    refreshSession,
    getTokens,
    hasTokens,
    clearCache,
  };
}

// Export for direct SDK access (advanced use cases)
export function getSdk(): CcPlatformSdk {
  if (!sdkInstance) {
    initializeSdk();
  }
  return sdkInstance!;
}
